<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card-text>
      <v-card class="mx-auto" elevation="0" title="Demographic baseline numbers"
              ref="demographic-baseline-numbers">
        <v-row no-gutters>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <v-sheet class="pa-2 ma-2">
              <div class="number-box">
                <div>
                  <span class="large-number">{{ demographicBaselineTotalNumberOfPatients.value }}</span>
                  <span class="percentage-number" v-if="demographicBaselineTotalNumberOfPatients.percentage">&nbsp;
                    {{ demographicBaselineTotalNumberOfPatients.percentage }}%
                  </span>
                </div>
                <div class="label large-number-label">{{ demographicBaselineTotalNumberOfPatients.label }}</div>
              </div>
            </v-sheet>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <v-sheet class="pa-2 ma-2">
              <div class="number-box-left">
                <v-data-table :headers="demographicBaselineNumbersHeaders" :items="demographicBaselineNumbers"
                              :fixed-header="true"
                              height="200px"
                              :items-per-page="0">
                  <template #bottom></template>
                  <template v-slot:[`item.percentage`]="{ item }">
                    <span style="color:#b2df8a">{{ item.percentage }}%</span>
                  </template>
                </v-data-table>
              </div>
            </v-sheet>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <v-sheet class="pa-2 ma-2">
              <div class="number-box">
                <div>
                  <span class="large-number">{{ demographicBaselineTotalTimeSpanInYears.value }}</span>
                </div>
                <div class="label large-number-label">{{ demographicBaselineTotalTimeSpanInYears.label }}</div>
              </div>
            </v-sheet>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <v-sheet class="pa-2 ma-2">
              <div class="number-box">
                <transition name="fade-bounce" mode="out-in">
                  <div :key="filteredDemographicBaselineTotalNumberOfPatients.value">
                    <span class="large-number">{{ filteredDemographicBaselineTotalNumberOfPatients.value }}</span>
                    <span class="percentage-number" v-if="filteredDemographicBaselineTotalNumberOfPatients.percentage">&nbsp;
                    {{ filteredDemographicBaselineTotalNumberOfPatients.percentage }}%
                  </span>
                  </div>
                </transition>
                <div class="label large-number-label">{{ filteredDemographicBaselineTotalNumberOfPatients.label }}</div>
              </div>
            </v-sheet>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <v-sheet class="pa-2 ma-2">
              <div class="number-box-left">
                <transition name="fade" mode="out-in">
                  <div :key="filteredDemographicBaselineNumbers">
                    <v-data-table :headers="demographicBaselineNumbersHeaders" :items="filteredDemographicBaselineNumbers"
                                  :fixed-header="true"
                                  height="200px"
                                  :items-per-page="0">
                      <template #bottom></template>
                      <template v-slot:[`item.percentage`]="{ item }">
                        <span style="color:#b2df8a">{{ item.percentage }}%</span>
                      </template>
                    </v-data-table>
                  </div>
                </transition>
              </div>
            </v-sheet>
          </v-col>
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <v-sheet class="pa-2 ma-2">
              <div class="number-box">
                <transition name="fade-bounce" mode="out-in">
                  <div :key="filteredDemographicBaselineTotalTimeSpanInYears.value">
                    <span class="large-number">{{ filteredDemographicBaselineTotalTimeSpanInYears.value }}</span>
                  </div>
                </transition>
                <div class="label large-number-label">{{ filteredDemographicBaselineTotalTimeSpanInYears.label }}</div>
              </div>
            </v-sheet>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col class="text-right">
            <vue-to-print :content="() => this.$refs['demographic-baseline-numbers'].$el"
                          :document-title="icd + '-' + 'demographic-baseline-numbers'"
                          remove-after-print>
              <template v-slot:trigger>
                <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
              </template>
            </vue-to-print>
          </v-col>
        </v-row>
      </v-card>
      <v-row no-gutters>
        <v-col cols="12" sm="10" md="8" lg="6" xl="6" xxl="6">
          <v-card class="mx-auto" min-height="800" elevation="0" title="Number of diagnosed patients"
                  ref="number-of-diagnosed-patients">
            <v-row no-gutters>
              <v-col>
                <GChart type="BarChart" :data="totalNumberOfPatientsPerYearChartData"
                        :options="totalNumberOfPatientsPerYearChartOptions"
                        v-if="totalNumberOfPatientsPerYearChartData.length !== 0"/>
                <v-chip variant="elevated" size="x-large" density="default" v-else>
                  No data to display. Please adjust your filters.
                </v-chip>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['number-of-diagnosed-patients'].$el"
                              :document-title="icd + '-' + 'number-of-diagnosed-patients'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="12" sm="10" md="8" lg="6" xl="6" xxl="6">
          <v-row no-gutters>
            <v-col>
              <v-card class="mx-auto" elevation="0" title="Metastasis status"
                      ref="metastasis-status"
                      v-if="(metastasisStatusM.length > 0) || (metastasisStatusN.length > 0)">
                <GChart type="PieChart" :data="metastasisMChartData"
                        :options="metastasisMChartOptions"
                        v-if="metastasisMChartData.length !== 0"/>
                <v-chip variant="elevated" size="x-large" density="default" v-else>
                  No data to display. Please adjust your filters.
                </v-chip>
                <GChart type="PieChart" :data="metastasisNChartData"
                        :options="metastasisNChartOptions"
                        v-if="metastasisNChartData.length !== 0"/>
                <v-chip variant="elevated" size="x-large" density="default" v-else>
                  No data to display. Please adjust your filters.
                </v-chip>
              </v-card>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col class="text-right" v-if="(metastasisStatusM.length > 0) || (metastasisStatusN.length > 0)">
              <vue-to-print :content="() => this.$refs['metastasis-status'].$el"
                            :document-title="icd + '-' + 'metastasis-status'"
                            remove-after-print>
                <template v-slot:trigger>
                  <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                </template>
              </vue-to-print>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import {GChart} from 'vue-google-charts'
import axios from "axios";
import { VueToPrint } from "vue-to-print";

export default defineComponent({
  name: 'CancerDemographicBaseline',
  props: {
    icd: String,
    ageAtDiagnosis: Array,
    diagnosisDate: Array,
    survivalTimeInYears: Array,
    metastasisStatusM: Array,
    metastasisStatusN: Array,
    atcCodes: Array,
    visible: Boolean
  },
  components: {
    GChart,
    VueToPrint
  },
  data: () => ({
    demographicBaselineTotalNumberOfPatients: {},
    demographicBaselineTotalTimeSpanInYears: {},
    demographicBaselineNumbersHeaders: [
      {
        title: 'Group',
        align: 'start',
        key: 'label',
      },
      {
        title: 'Value',
        align: 'end',
        key: 'value',
      },
      {
        title: 'Percentage',
        align: 'end',
        key: 'percentage',
      }],
    demographicBaselineNumbers: [],
    filteredDemographicBaselineTotalNumberOfPatients: {},
    filteredDemographicBaselineTotalTimeSpanInYears: {},
    filteredDemographicBaselineNumbers: [],
    totalNumberOfPatientsPerYearChartData: [
    ],
    totalNumberOfPatientsPerYearChartOptions: {
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#a6cee3'],
      legend: 'none',
      hAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        textStyle: {
          color: '#ffffff'
        },
        gridlines: {
          color: '#c0c0c0'
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        },
        textStyle: {
          color: '#ffffff'
        }
      },
      annotations: {
        textStyle: {
          fontSize: 16,
          color: '#808080',
          auraColor: 'none'
        }
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    },
    metastasisMChartData: [],
    metastasisMChartOptions: {
      title: 'Metastasis status (M)',
      pieSliceText: 'value',
      is3D: false,
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 400,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: {
        position: 'bottom',
        alignment: 'end',
        maxLines: 5,
        textStyle: {
          color: '#c0c0c0',
          fontSize: 12
        },
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      }
    },
    metastasisNChartData: [],
    metastasisNChartOptions: {
      title: 'Metastasis status (N)',
      pieSliceText: 'value',
      is3D: false,
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 400,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: {
        position: 'bottom',
        alignment: 'end',
        maxLines: 5,
        textStyle: {
          color: '#c0c0c0',
          fontSize: 12
        },
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      }
    }
  }),
  methods: {
    async loadDemographicBaselineTotalNumberOfPatientsData() {
      let data = axios.get('cancer-demographic-baseline-total-number-of-patients',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.demographicBaselineTotalNumberOfPatients = response.data;
      });
    },
    async loadDemographicBaselineTotalTimeSpanInYearsData() {
      let data = axios.get('cancer-demographic-baseline-total-time-span-in-years',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.demographicBaselineTotalTimeSpanInYears = response.data;
      });
    },
    async loadDemographicBaselineData() {
      let data = axios.get('cancer-demographic-baseline',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.demographicBaselineNumbers = response.data;
      });
    },
    async loadFilteredDemographicBaselineTotalNumberOfPatientsData() {
      return axios.get('cancer-filtered-demographic-baseline-total-number-of-patients',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadFilteredDemographicBaselineTotalTimeSpanInYearsData() {
      return axios.get('cancer-filtered-demographic-baseline-total-time-span-in-years',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadFilteredDemographicBaselineData() {
      return axios.get('cancer-filtered-demographic-baseline',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadTotalNumberOfPatientsPerYearChartData() {
      return axios.get('cancer-total-number-of-patients-per-year-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadMetastasisMData() {
      return axios.get('cancer-metastasis-m-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadMetastasisNData() {
      return axios.get('cancer-metastasis-n-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadDynamicData() {
      return await Promise.all([
        this.loadFilteredDemographicBaselineTotalNumberOfPatientsData(),
        this.loadFilteredDemographicBaselineTotalTimeSpanInYearsData(),
        this.loadFilteredDemographicBaselineData(),
        this.loadTotalNumberOfPatientsPerYearChartData(),
        this.loadMetastasisMData(),
        this.loadMetastasisNData()
      ]).then(responses => {
        this.filteredDemographicBaselineTotalNumberOfPatients = responses[0].data
        this.filteredDemographicBaselineTotalTimeSpanInYears = responses[1].data
        this.filteredDemographicBaselineNumbers = responses[2].data
        this.totalNumberOfPatientsPerYearChartData = responses[3].data
        this.metastasisMChartData = responses[4].data
        this.metastasisNChartData = responses[5].data
        console.log('Dynamic data loaded: CancerDemographicBaseline')
      })
    }
  },
  async mounted() {
    await Promise.all([
      this.loadDemographicBaselineTotalNumberOfPatientsData(),
      this.loadDemographicBaselineTotalTimeSpanInYearsData(),
      this.loadDemographicBaselineData()
    ]).then(() => {
      this.loadDynamicData()
    })
  },
  updated() {
    if (this.visible) {
      this.loadDynamicData()
    }
  }
});
</script>

<style scoped>
.number-box {
  height: 250px;
  justify-content: space-around;
  text-align: center;
  padding: 50px;
  margin: 10px;
  flex: 1;
  border-radius: 8px;
  background-color: #364c5b;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px #c0c0c0; /* Elevated border effect */
}

.number-box-left {
  height: 250px;
  justify-content: space-around;
  text-align: left;
  padding: 20px;
  margin: 10px;
  flex: 1;
  border-radius: 8px;
  background-color: #364c5b;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px #c0c0c0; /* Elevated border effect */
}

.large-number {
  font-size: 4em;
  font-weight: bold;
}

.percentage-number {
  font-size: 2em;
  color: #b2df8a;
}

.large-number-label {
  font-size: 1em;
  margin-top: 10px;
}

.fade-bounce-enter-active {
  transition: all 0.5s ease-in;
  animation: bounce-in 0.5s;
}
.fade-bounce-leave-active {
  transition: all 0.5s ease-out;
  animation: bounce-in 0.5s reverse;
}

.fade-bounce-enter-from,
.fade-bounce-leave-to {
  opacity: 0;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.25);
  }
  100% {
    transform: scale(1);
  }
}

@media print {
  .mx-auto {
    zoom: 50%;
  }
}
</style>
